/**
 * BatchResultsExport Component
 *
 * CSV export utility and button for batch underwriting results.
 *
 * Features:
 *  - Imports BatchPropertyResult from types/batch (authoritative — NOT services/api)
 *  - Enhanced CSV columns: Rank, Composite Score, Zandi Composite, Offer Range Low/High,
 *    IRR, EM, Verdict, Avg CoC, Stage, Market
 *  - Rank is positional (index + 1 in the passed array, already sorted/filtered)
 *  - Browser download via Blob/link
 *  - Disabled state when no results
 */

import { Download } from 'lucide-react';
import type { BatchPropertyResult } from '../../types/batch';

// ---------------------------------------------------------------------------
// Formatters (CSV-safe: plain values, no abbreviation)
// ---------------------------------------------------------------------------

function formatCurrencyCSV(value: number | null | undefined): string {
  if (value == null) return '';
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatPercentCSV(value: number | null | undefined): string {
  if (value == null) return '';
  // Backend may return either decimal (0.15) or percentage (15.0)
  if (Math.abs(value) > 1) {
    return `${value.toFixed(2)}%`;
  }
  return `${(value * 100).toFixed(2)}%`;
}

function escapeCSV(value: string | number | null | undefined): string {
  if (value == null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

// ---------------------------------------------------------------------------
// Export function
// ---------------------------------------------------------------------------

/**
 * Export batch results to CSV file.
 *
 * Results are passed in already sorted/filtered order — Rank = index + 1.
 */
export function exportBatchResultsToCSV(
  results: BatchPropertyResult[],
  filename?: string
): void {
  if (!results || results.length === 0) {
    console.warn('No results to export');
    return;
  }

  // CSV header columns (order matches PLAN 02 spec)
  const headers = [
    'Rank',
    'Address',
    'Neighborhood',
    'Composite Score',
    'IRR 5yr',
    'Equity Multiple',
    'Cap Rate',
    'Zandi Composite',
    'NOI',
    'Offer Range Low',
    'Offer Range High',
    'Units',
    'Market',
    'Verdict',
    'Stage',
    'Avg CoC',
  ];

  // Build CSV rows
  const rows = results.map((result, idx) => [
    String(idx + 1),                                              // Rank
    escapeCSV(result.address),                                    // Address
    escapeCSV(result.neighborhood),                               // Neighborhood
    result.composite_score != null ? result.composite_score.toFixed(1) : '', // Composite Score
    formatPercentCSV(result.irr_5yr),                            // IRR 5yr
    result.equity_multiple != null ? `${result.equity_multiple.toFixed(2)}x` : '', // EM
    formatPercentCSV(result.cap_rate),                            // Cap Rate
    result.zandi_composite != null ? result.zandi_composite.toFixed(1) : '',  // Zandi Composite
    formatCurrencyCSV(result.noi),                                // NOI
    formatCurrencyCSV(result.offer_range_low),                    // Offer Range Low
    formatCurrencyCSV(result.offer_range_high),                   // Offer Range High
    result.total_units?.toString() ?? '',                         // Units
    escapeCSV(result.neighborhood),                               // Market (neighborhood as proxy)
    escapeCSV(result.deal_verdict),                               // Verdict
    result.stage?.toString() ?? '',                               // Stage
    formatPercentCSV(result.avg_coc),                             // Avg CoC
  ]);

  // Combine header + rows
  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.join(',')),
  ].join('\n');

  // Create Blob and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const date = new Date().toISOString().split('T')[0];
  const defaultFilename = filename ?? `batch-underwriting-${date}.csv`;

  const link = document.createElement('a');
  link.href = url;
  link.download = defaultFilename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BatchResultsExportProps {
  /** Currently visible (filtered+sorted) results — Rank = index + 1 */
  results: BatchPropertyResult[];
  /** Optional disabled state */
  disabled?: boolean;
  /** Optional submarket name to include in filename */
  submarketName?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BatchResultsExport({
  results,
  disabled,
  submarketName,
}: BatchResultsExportProps) {
  const handleExport = () => {
    if (!results || results.length === 0) return;

    const date = new Date().toISOString().split('T')[0];
    const sanitizedSubmarket = submarketName
      ? submarketName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      : null;

    const filename = sanitizedSubmarket
      ? `${sanitizedSubmarket}-batch-${date}.csv`
      : `batch-underwriting-${date}.csv`;

    exportBatchResultsToCSV(results, filename);
  };

  const isDisabled = disabled || !results || results.length === 0;
  const count = results?.length ?? 0;

  return (
    <button
      onClick={handleExport}
      disabled={isDisabled}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-bg-card border-border text-text-secondary hover:border-accent/50 hover:text-accent"
    >
      <Download className="w-3.5 h-3.5" />
      Export CSV ({count})
    </button>
  );
}

export default BatchResultsExport;
